import { useEffect, useState } from "react";
import { useRuntimeContext } from "../features/tutor/context/RuntimeContext";
import VoDemo from "@/page/VoDemo";
import { tutorApi } from "../utils/axios-utils";
import { useSessionStore } from "../features/tutor/store/useSessionStore";
import { useSlideStore } from "../features/tutor/store/useSlideStore";
import PrimaryButton from "@/components/PrimaryButton";
import SecondaryButton from "@/components/SecondaryButton";
import Dropdown from "@/components/Dropdown";
import { debugLog } from "@/utils/debugLog";
import { startTutorTurn } from "@/features/tutor/utils/tutorTurnLifecycle";

const SlideEditor = () => {
  const [error, setError] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [metaPrompt, setMetaPrompt] = useState("");
  const [isLessonDropdownOpen, setIsLessonDropdownOpen] = useState(false);
  const [isSlideDropdownOpen, setIsSlideDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { emit } = useRuntimeContext();
  const { lessons, slides, fetchLessons, fetchSlidesByLesson } =
    useSlideStore();
  useEffect(() => {
    debugLog("Fetching lessons...");
    fetchLessons();
  }, [fetchLessons]);

  useEffect(() => {
    if (currentLessonId) {
      debugLog("Fetching slides for lesson:", currentLessonId);
      fetchSlidesByLesson(currentLessonId);
    }
  }, [currentLessonId, fetchSlidesByLesson]);

  useEffect(() => {
    debugLog("Lessons state:", lessons);
    debugLog("Slides state:", slides);
  }, [lessons, slides]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const currentSessionId = useSessionStore.getState().sessionId;

      emit("TURN_CHANGE_AND_SLIDE_UPDATE", currentIndex);

      await startTutorTurn((tutor_audio_on) =>
        tutorApi.post(
          `/session/update_and_start_session_lesson_slide/${currentSessionId}`,
          {
            slide_index: currentIndex,
            raw_slide_prompt: metaPrompt,
            lesson_id: currentLessonId,
            tutor_audio_on,
          },
        ),
      );
      debugLog("Slide updated successfully");
      setError("");
    } catch (error) {
      console.error("Failed to update slide:", error);
      setError(error.response?.data?.detail || "Failed to update slide");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsLoading(true);
      emit("TURN_CHANGE_AND_SLIDE_UPDATE", currentIndex);
      setError("");
    } catch (error) {
      console.error("Failed to reset slide:", error);
      setError("Failed to reset slide");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setError("");
    const updateSlideIndex = async () => {
      emit("TURN_CHANGE_AND_SLIDE_UPDATE", currentIndex);
    };

    updateSlideIndex();
  }, [currentIndex]);

  const handleLessonSelect = (lessonId) => {
    setCurrentLessonId(lessonId);
    setCurrentIndex(0);
    setIsLessonDropdownOpen(false);
    setIsSlideDropdownOpen(false);
  };

  const selectedLesson = lessons.find(
    (lesson) => lesson.id === currentLessonId,
  );
  const selectedSlide = slides.find(
    (slide) => slide.slide_index === currentIndex,
  );

  return (
    <div className="grid grid-cols-[1fr_2fr] grid-rows-[auto_1fr] p-4 gap-4 w-full h-screen">
      {/* Left Column */}
      <div className="col-[1] row-[1] flex flex-col items-center justify-center">
        <h1 className="text-2xl font-semibold text-darkgreen mt-auto">
          Tutor Content Editor
        </h1>
      </div>
      <div className="col-[1] row-[2] flex flex-col p-8 rounded-3xl border-2 border-darkgreen overflow-auto m-4">
        <div className="p-4 flex flex-col gap-4 h-full">
          <Dropdown
            title={
              selectedLesson ? selectedLesson.name : "Select a lesson to begin"
            }
            isOpen={isLessonDropdownOpen}
            onToggle={() => setIsLessonDropdownOpen(!isLessonDropdownOpen)}
          >
            {lessons.map((lesson) => (
              <div
                key={lesson.id}
                className="text-lightgreen p-3 cursor-pointer hover:bg-lightgreen/10"
                onClick={() => handleLessonSelect(lesson.id)}
              >
                {lesson.name}
              </div>
            ))}
          </Dropdown>

          {selectedLesson && (
            <Dropdown
              title={
                selectedSlide
                  ? selectedSlide.name || `Slide ${currentIndex}`
                  : "Select a slide"
              }
              isOpen={isSlideDropdownOpen}
              onToggle={() => setIsSlideDropdownOpen(!isSlideDropdownOpen)}
            >
              {!slides || slides.length === 0 ? (
                <div className="p-3 text-gray-500 text-center">
                  {!slides
                    ? "Loading slides..."
                    : "No slides found for this lesson"}
                </div>
              ) : (
                slides.map((slide) => (
                  <div
                    key={slide.id}
                    className="text-lightgreen p-3 cursor-pointer hover:bg-lightgreen/10 border-b border-gray-100 last:border-b-0"
                    onClick={() => setCurrentIndex(slide.slide_index)}
                  >
                    {slide.name || `Slide ${slide.slide_index}`}
                  </div>
                ))
              )}
            </Dropdown>
          )}

          <div className="flex-1 flex flex-col min-h-0">
            <p className="text-darkgreen">Change System Prompt</p>
            <textarea
              className="w-full p-2 border-2 border-darkgreen rounded-md flex-1 resize-none"
              value={metaPrompt}
              onChange={(e) => setMetaPrompt(e.target.value)}
            />
          </div>

          <div className="flex flex-row gap-4 justify-between">
            <PrimaryButton
              onClick={handleSubmit}
              disabled={!currentLessonId || !selectedSlide || isLoading}
            >
              Submit
            </PrimaryButton>
            <SecondaryButton
              onClick={handleReset}
              disabled={!currentLessonId || !selectedSlide || isLoading}
            >
              Reset
            </SecondaryButton>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="col-[2] row-[1] flex flex-col items-center justify-center">
        <p className="text-gray-400">Currently editing</p>
        <h1 className="text-2xl font-semibold text-black">
          {selectedSlide
            ? selectedSlide.name || `Slide ${currentIndex}`
            : "No slide selected"}
        </h1>
      </div>
      <div className="col-[2] row-[2] rounded-3xl overflow-auto m-4 relative">
        {currentLessonId ? (
          <VoDemo lessonId={currentLessonId} />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500 text-xl">
            Please select a lesson to view slides
          </div>
        )}
        <div
          className={`absolute inset-0 flex items-center justify-center bg-white/30 z-50 transition-all duration-300 ease-in-out ${isLoading ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        >
          <svg
            className="animate-spin h-12 w-12 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default SlideEditor;
